import React, { useCallback, useMemo, useState } from "react";
import * as store from "../../framework/customStore";
import * as storeActions from "../../models/store/storeActions";
import { useParams } from "react-router-dom";
import { MuiDataGrid } from "../framework/muiDataGrid";
import { IWorkTimeDetailsPage } from "./workTimeBetaDetailsPageMain";
import {
    DayBookingItemBeta,
    getDurationStrBasedOnUnit,
} from "../../models/dayBooking/dayBookingItemBeta";
import { DayBookingTypeItemBeta } from "../../models/dayBookingType/dayBookingTypeItemBeta";
import * as dayBookingService from "../../services/dayBookingService";
import * as baseService from "../../services/baseService";
import {
    GridColDef,
    GridRenderCellParams,
    GridValueFormatterParams,
    GridValueGetterParams,
} from "@mui/x-data-grid-premium";
import { Translations } from "../../models/translations";
import { Base } from "../../framework/base";
import MuiMenu from "../framework/muiMenu";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import HdrAutoIcon from "@mui/icons-material/HdrAuto";
import { ConfirmationDialogType } from "../../models/store/storeTypes";
import {
    Edit as EditIcon,
    Delete as DeleteIcon,
    Lock as LockIcon,
    LockOpen as LockOpenIcon,
} from "@mui/icons-material";
import {
    DayBookingState,
    EnumHelper,
    StrMeasureUnit,
} from "../../models/common/enums";
import { Tooltip, useTheme } from "@mui/material";
import { LoadingIndicator } from "../framework/loadingIndicatorNew";
import {
    DayBookingListRow,
    dayBookingToListRow,
    workHourToListRow,
} from "../../models/workTime/workTimeDetailsDayBookingList";
import { IWorkHoursItem } from "../../models/workTime/workHoursItem";
import { useTranslation } from "react-i18next";
import { deleteCategoryFromWorkShiftTimeSlot } from "../../services/workTimeBetaService";

interface ActionCellProps {
    handleEdit: (id: string) => void;
    handleDelete: (id: string) => void;
    handleLock: (ids: string[], lock: boolean) => void;
}

function ActionCell(props: GridRenderCellParams & ActionCellProps) {
    const {
        value,
        row,
        handleEdit,
        handleDelete,
        handleLock,
    } = props;
    const theme = useTheme();
    const { t } =useTranslation();
    const isLocked = row.state === DayBookingState.Locked;
    const items = isLocked
        ? [
            {
                label: Translations.DoUnlockAll,
                icon: <LockOpenIcon color="primary" />,
                onClick: () => handleLock([value], false),
            },
        ]
        : [
            {
                label: Translations.Edit,
                icon: <EditIcon color="primary" />,
                onClick: () => handleEdit(value),
            },
            {
                label: Translations.DoLockAll,
                icon: <LockIcon color="primary" />,
                onClick: () => handleLock([value], true),
                disabled: row.isAutomatic,
                tooltip: row.isAutomatic
                    ? t("dayBooking.automaticEntryLockingDisabled")
                    : null,
            },
            { divider: true },
            {
                label: Translations.Remove,
                icon: <DeleteIcon color="error" />,
                onClick: () => handleDelete(value),
            },
        ];

    return (
        <MuiMenu
            label={
                isLocked ? (
                    <LockIcon
                        sx={{
                            color: theme.palette.primary.main,
                        }}
                    />
                ) : (
                    <MoreHorizIcon
                        sx={{
                            color: theme.palette.primary.main,
                        }}
                    />
                )
            }
            items={items}
        />
    );
}

interface IWorkTimeDetailsDayBookingListProps {
    setEditDayBookingItem: React.Dispatch<React.SetStateAction<DayBookingItemBeta>>;
    reloadList: () => void;
    dayBookingItems: DayBookingItemBeta[];
    dayBookingTypes: DayBookingTypeItemBeta[];
    shiftDailyAllowances: IWorkHoursItem[];
}

export function WorkTimeDetailsDayBookingList({
    setEditDayBookingItem,
    reloadList,
    dayBookingItems,
    dayBookingTypes,
    shiftDailyAllowances,
}: IWorkTimeDetailsDayBookingListProps) {
    const [loading, setLoading] = useState(false);
    const { employeeId } = useParams<IWorkTimeDetailsPage>();
    const allLocked =
        dayBookingItems.length > 0 &&
        dayBookingItems.every((i) =>
            EnumHelper.isEqual(i.state, DayBookingState.Locked)
        );
    const { t } = useTranslation();

    const vehicleOptions = store.useAppSelector(
        (state) => state.vehicle.options
    );
    const costCenterOptions = store.useAppSelector(
        (state) => state.costCenter.groupedOptions
    );

    const rows: DayBookingListRow[] = useMemo(
        () => [
            ...(dayBookingItems ?? []).map(dayBookingToListRow),
            ...(shiftDailyAllowances ?? []).map((a) =>
                workHourToListRow(
                    a,
                    costCenterOptions,
                    vehicleOptions,
                    dayBookingTypes
                )
            ),
        ],
        [
            dayBookingItems,
            shiftDailyAllowances,
            vehicleOptions,
            costCenterOptions,
            dayBookingTypes,
        ]
    );

    const handleDelete = useCallback(
        (id: string) => {
            if (!id) return;
            const row = rows.find((r) => r.id === id);
            if (!row) return;

            store.customStore.dispatch(
                storeActions.setConfirmation(
                    ConfirmationDialogType.Warning,
                    Translations.Warning,
                    Translations.DayBookingRemoveConfirmation,
                    () => {
                        store.customStore.dispatch(storeActions.clearConfirmation());
                        // Call server
                        setLoading(true);

                        (row.isAutomatic
                            ? deleteCategoryFromWorkShiftTimeSlot(
                                row.workShiftTimeSlotId,
                                row.id.split(".")[1],
                                false
                            )
                            : dayBookingService.deleteDayBooking(id)
                        )
                            .then(() => {
                                store.customStore.dispatch(
                                    storeActions.showSuccessMessage(
                                        Translations.DayBookingRemoveSuccess
                                    )
                                );
                                reloadList();
                            })
                            .catch((error) => {
                                store.customStore.dispatch(
                                    storeActions.showErrorMessage(
                                        baseService.getErrorMessageFromError(
                                            error
                                        )
                                    )
                                );
                                return null;
                            })
                            .finally(() => setLoading(false));
                    },
                    () => {
                        store.customStore.dispatch(storeActions.clearConfirmation());
                    }
                )
            );
        }, [reloadList, rows]
    );

    const handleEdit = useCallback(
        (id: string) => {
            let item = dayBookingItems.find((i) => i.id === id);

            // If item is automatic, it's not present in dayBookingItems
            if (!item) {
                const row = rows.find((r) => r.id === id);
                if (!row || !row.isAutomatic) return;

                const {
                    dayBookingTypeId,
                    costCenterId,
                    costCenterName,
                    vehicleId,
                    vehicleName,
                    day,
                    endDay,
                    amount,
                    dayUsageType,
                    comment,
                    workShiftTimeSlotId,
                } = row;

                item = new DayBookingItemBeta({
                    employeeId,
                    dayBookingTypeId,
                    costCenterId,
                    costCenterName,
                    vehicleId,
                    vehicleName,
                    day,
                    endDay,
                    amount,
                    dayUsageType,
                    comment,
                    automaticCategory: id.split(".")[1],
                    automaticWorkShiftTimeSlotId: workShiftTimeSlotId,
                });
            }

            setEditDayBookingItem(item);
        },
        [dayBookingItems, setEditDayBookingItem, employeeId, rows]
    );

    const handleLock = useCallback((ids: string[], lock: boolean) => {
        if (ids.length === 0) return;
        setLoading(true);
        (lock
            ? dayBookingService.lockDayBookings(ids)
            : dayBookingService.unLockDayBookings(ids)
        )
            .then((success) => {
                store.customStore.dispatch(
                    storeActions.showSuccessMessage(success.message)
                );
                reloadList();
            })
            .catch((error) => {
                store.customStore.dispatch(
                    storeActions.showErrorMessage(
                        baseService.getErrorMessageFromError(error)
                    )
                );
                return null;
            })
            .finally(() => setLoading(false));
    }, []);

    interface IDayColumnValueGetter {
        day: string;
        duration: string;
        label: string;
    }
    const cols: GridColDef[] = useMemo(
        () => [
            {
                field: "day",
                headerName: Translations.Time,
                flex: 1,
                valueGetter: (
                    params: GridValueGetterParams<DayBookingListRow>
                ) => {
                    return {
                        day: params.row.day,
                        duration: Math.floor(params.row.amount),
                        label: `${Base.dayjsToDateStr(
                            params.row.day
                        )}-${Base.dayjsToDateStr(params.row.endDay)}`,
                    };
                },
                valueFormatter: (
                    params: GridValueFormatterParams<IDayColumnValueGetter>
                ) => params.value?.label,
                sortComparator: (a, b) =>
                    Base.stringCompare(a.day, b.day) ||
                    b.dayUsageType - a.dayUsageType ||
                    Base.numberCompare(a.duration, b.duration),
            },
            {
                field: "amount",
                valueGetter: (
                    params: GridValueGetterParams<DayBookingListRow>
                ) => {
                    const dayBookingType =
                        dayBookingTypes &&
                        dayBookingTypes.find(
                            (type) => type?.id === params.row?.dayBookingTypeId
                        );
                    if (!dayBookingType) return params.row.amount;

                    const duration = getDurationStrBasedOnUnit(
                        params.row.amount,
                        params.row.dayUsageType,
                        dayBookingType.dayHoursRatio,
                        dayBookingType.salaryRowTypeMeasureUnit
                    );
                    const unit =
                        dayBookingType?.salaryRowTypeMeasureUnit ||
                        StrMeasureUnit.Day;
                    if (duration && unit) return `${duration} (${unit})`;
                    return `${duration} `;
                },
                valueFormatter: (params: GridValueFormatterParams<string>) => {
                    return params.value;
                },
                headerName: `${Translations.Duration}`,
                flex: 1,
            },
            {
                field: "dayBookingTypeId",
                headerName: Translations.DayBookingType,
                flex: 2,
                valueGetter: (params: GridValueGetterParams) =>
                    dayBookingTypes?.find(
                        (i) => i.id === params.row.dayBookingTypeId
                    )?.name,
                renderCell: (
                    params: GridRenderCellParams<DayBookingListRow>
                ) => (
                    <>
                        {params.row.isAutomatic ? (
                            <Tooltip
                                title={t("dayBooking.automatic", {
                                    label: (
                                        (params.formattedValue as string) ?? ""
                                    ).toLowerCase(),
                                })}
                            >
                                <HdrAutoIcon color="primary" sx={{ mr: 0.5 }} />
                            </Tooltip>
                        ) : null}
                        {params.formattedValue}
                    </>
                ),
            },
            {
                field: "costCenterName",
                headerName: Translations.CostCenter,
                flex: 2,
            },
            {
                field: "vehicleName",
                headerName: Translations.Vehicle,
                flex: 2,
            },
            {
                field: "comment",
                headerName: Translations.Description,
                flex: 2,
            },
            {
                field: "id",
                headerName: "",
                width: 75,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                renderCell: (params: GridRenderCellParams) => (
                    <ActionCell
                        {...params}
                        handleLock={handleLock}
                        handleEdit={handleEdit}
                        handleDelete={handleDelete}
                    />
                ),
            },
        ],
        [dayBookingTypes, handleEdit, handleLock, handleDelete]
    );

    if (!(rows?.length > 0)) {
        return null;
    }

    return (
        <>
            <LoadingIndicator loading={loading} />
            <MuiDataGrid
                density="compact"
                title={Translations.DayBookings}
                disableRowSelectionOnClick
                autoHeight
                rows={rows}
                columns={cols}
                hideFooter
                disableColumnMenu
                disableVirtualization
                initialState={{
                    sorting: {
                        sortModel: [
                            {
                                field: "startDate",
                                sort: "asc",
                            },
                        ],
                    },
                }}
                customToolbarButtons={
                    dayBookingItems?.length > 0
                        ? [
                            {
                                key: "lock",
                                startIcon: allLocked ? (
                                    <LockOpenIcon />
                                ) : (
                                    <LockIcon />
                                ),
                                onClick: () =>
                                    handleLock(
                                        dayBookingItems.map((i) => i.id),
                                        !allLocked
                                    ),
                                children: allLocked
                                    ? Translations.UnlockAll
                                    : Translations.LockAll,
                            },
                        ]
                        : null
                }
            />
        </>
    );
}
